/**
 * Posture Analysis Mode Strategy
 *
 * Defines the behaviour contract for different posture analysis contexts.
 * Instead of scattering `if (isSelfAssessment)` checks throughout the
 * codebase, each mode is represented by a concrete strategy object that
 * controls feature gates, auth behaviour, allowed views, and UI customisation.
 *
 * The active strategy is delivered to the component tree via
 * `PostureModeContext` (see PostureModeContext.tsx).
 */

import type { CaptureSequence } from '../types';
import type { WizardMessageOverrides } from '../GuidedCaptureWizard';
import { SELF_ASSESSMENT_WIZARD_OVERRIDES } from '../GuidedCaptureWizard';
import { DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID } from '@/lib/posture-analysis/posture-pic-evaluation/strategies/posture-orientation-strategy-id';

// ---------------------------------------------------------------------------
// Strategy interface
// ---------------------------------------------------------------------------

export type PostureMode = 'practitioner' | 'self-assessment';

export interface PostureModeStrategy {
  /** Discriminant — useful for logging / analytics. */
  readonly mode: PostureMode;

  // ── Feature gates ──────────────────────────────────────────────────────
  /** Can persist an analysis to Firestore (requires client + scopedDb). */
  readonly canSave: boolean;
  /** Can load a previously saved analysis for the current client. */
  readonly canLoad: boolean;
  /** Can open the side-by-side comparison tab. */
  readonly canCompare: boolean;
  /** Can trigger the professional PDF export (toolbar button). */
  readonly canExportPdf: boolean;
  /** Can open the "Select / Create Client" dialog. */
  readonly canSelectClient: boolean;
  /** Can toggle the analysis tiles / panel sidebar. */
  readonly canShowAnalysisPanel: boolean;

  // ── View restrictions ──────────────────────────────────────────────────
  /** Views the user is allowed to capture in this mode. */
  readonly allowedViews: readonly CaptureSequence[];

  // ── Auth behaviour ─────────────────────────────────────────────────────
  /** Whether the mode requires a valid Firebase session. */
  readonly requiresAuth: boolean;
  /** Whether to call /api/auth/session on view changes (perf + security). */
  readonly shouldCheckSession: boolean;

  // ── UI customisation ───────────────────────────────────────────────────
  /** Optional plain-language overrides for the guided capture wizard steps. */
  readonly wizardOverrides?: WizardMessageOverrides;
  /**
   * When true, all classification UI uses plain everyday language instead of
   * clinical terminology (e.g. "Forward-tilted pelvis" instead of "Anterior
   * Pelvic Tilt", "First focus:" instead of "Phase 1 Goal:").
   * Automatically enabled for self-assessment; off for the practitioner mode.
   */
  readonly laymanMode: boolean;
  /**
   * Override for experimental features toggle:
   *  - `null` → defer to `appSettings.experimentalFeaturesEnabled`
   *  - `false` → force experimental features off
   */
  readonly experimentalFeaturesOverride: boolean | null;
}

/** Whether ASIS/PSIS pin markers render on capture/report imagery. */
export function shouldShowPelvisOverlay(mode: PostureModeStrategy): boolean {
  return mode.mode !== 'self-assessment';
}

/**
 * Whether to drop the Pelvic Tilt *metric row* (including the hip–knee–ankle estimate).
 *
 * This is not “skip the ASIS/PSIS pin line”. That line is used only when both
 * pins exist (`custom.ASIS` + `custom.PSIS` → `buildAsisPsisLineForSide`).
 * With no manual pins the line is already unused; `analyzeLateral` may still
 * write an estimated “Pelvic Tilt (ASIS-PSIS) … (Est.)” from thigh geometry.
 *
 * - Public `/self-assessment` omits the whole row (guests cannot place pins).
 * - Practitioner / client app keeps the estimate unless `pelvisPinsOptOut`.
 */
export function shouldOmitPelvicTilt(
  mode: PostureModeStrategy,
  image?: { pelvisPinsOptOut?: boolean } | null,
): boolean {
  if (mode.mode === 'self-assessment') return true;
  return !!image?.pelvisPinsOptOut;
}

/** Respects mode override (e.g. self-assessment always off) before app settings. */
export function resolveExperimentalFeaturesEnabled(
  mode: PostureModeStrategy,
  appSettingsEnabled: boolean | undefined,
): boolean {
  if (mode.experimentalFeaturesOverride !== null) {
    return mode.experimentalFeaturesOverride;
  }
  return appSettingsEnabled === true;
}

// ---------------------------------------------------------------------------
// Concrete strategies
// ---------------------------------------------------------------------------

/**
 * Full practitioner / clinical mode.
 * All features enabled; auth required; all views available.
 */
export const PRACTITIONER_STRATEGY: PostureModeStrategy = {
  mode: 'practitioner',

  canSave: true,
  canLoad: true,
  canCompare: true,
  canExportPdf: true,
  canSelectClient: true,
  canShowAnalysisPanel: true,

  allowedViews: ['Anterior', 'Posterior', 'Left', 'Right'] as const,

  requiresAuth: true,
  shouldCheckSession: true,

  wizardOverrides: undefined,
  laymanMode: false,
  experimentalFeaturesOverride: null, // defer to appSettings
};

/**
 * Public self-assessment mode.
 * Restricted features; no auth; 3 views only (no Posterior).
 */
/** Same orientation engine as practitioner default (`adaptiveThresholdVprOverrideV5`). */
export const SELF_ASSESSMENT_POSTURE_ORIENTATION_STRATEGY_ID =
  DEFAULT_POSTURE_ORIENTATION_STRATEGY_ID;

export const SELF_ASSESSMENT_STRATEGY: PostureModeStrategy = {
  mode: 'self-assessment',

  canSave: false,
  canLoad: false,
  canCompare: false,
  canExportPdf: false, // PDF is handled by the self-assessment page, not the practitioner toolbar
  canSelectClient: false,
  canShowAnalysisPanel: true,

  allowedViews: ['Anterior', 'Right'] as const,

  requiresAuth: false,
  shouldCheckSession: false,

  wizardOverrides: SELF_ASSESSMENT_WIZARD_OVERRIDES,
  laymanMode: true,
  experimentalFeaturesOverride: false,
};
